import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useProjectPeriod } from "../useProjectPeriod";
import useGroups from "../../../api/useGroups";
import type { Source } from "../../../api/useSources";
import type { Project } from "../../../api/useProjects";
import DateRangePicker from "@ui/DateRangePicker/DateRangePicker";
import {
  parseAbsolute,
  toCalendarDateTime,
  today,
  toZoned,
  type DateValue,
} from "@internationalized/date";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import { baseAggregationPeriodPresets } from "../../../domain/aggregation/preset";
import { normalizeAggregationDateRange } from "../../../domain/aggregation/period";

const PeriodSelector = memo(
  ({
    source,
    project,
    analyses,
    boundaries,
  }: { project: Project; source: Source } & (
    | { analyses?: never[]; boundaries: "analyses" }
    | { analyses: ActiveAnalysis[]; boundaries: "active-analyses" }
  )) => {
    const [dateTimeError, setDateTimeError] = useState<string>();
    const [dateTimeInfo, setDateTimeInfo] = useState<string>();

    const [period, setPeriod, extremes] = useProjectPeriod(
      boundaries === "active-analyses"
        ? {
            source,
            project,
            analyses,
            boundaries: "active-analyses",
          }
        : {
            source,
            project,
            boundaries: "analyses",
          },
    );

    const dateBoundaries = useMemo(
      () => ({
        startDate: parseAbsolute(new Date(extremes.from).toISOString(), project.timezone),
        endDate: parseAbsolute(new Date().toISOString(), project.timezone),
      }),
      [extremes, project.timezone],
    );

    const [dateRangeValue, setDateRangeValue] = useState(() => ({
      start: parseAbsolute(new Date(period.from).toISOString(), project.timezone),
      end: parseAbsolute(new Date(period.to).toISOString(), project.timezone),
    }));

    const groups = useGroups(project.id);
    const presets = baseAggregationPeriodPresets({
      limit: { from: new Date(extremes.from), to: new Date() },
      timezone: project.timezone,
      groups,
    });

    const onDateValueChange = useCallback(
      (value: { start: DateValue; end: DateValue } | null) => {
        setDateTimeError(undefined);
        setDateTimeInfo(undefined);

        if (!value) {
          const preset = presets.find(p => p.metadata?.["id"] === "ALL");
          if (!preset) return;

          const presetStart =
            typeof preset.startDate === "function" ? preset.startDate() : preset.startDate;

          const presetEnd =
            typeof preset.endDate === "function" ? preset.endDate() : preset.endDate;

          const date = {
            start: toZoned(presetStart, project.timezone),
            end: toZoned(presetEnd, project.timezone),
          };
          setDateRangeValue(date);

          setPeriod({
            from: toCalendarDateTime(dateBoundaries.startDate).toDate(project.timezone).getTime(),
            to: toCalendarDateTime(dateBoundaries.endDate).toDate(project.timezone).getTime(),
          });

          return;
        }

        const { start: startDate, end: endDate } = normalizeAggregationDateRange(
          value,
          dateRangeValue,
          {
            min: dateBoundaries.startDate,
            max: dateBoundaries.endDate,
          },
        );

        // Note: min/max limiting is enforced via DateRangePicker dateOptions (minValue, maxValue, isDateUnavailable).

        setDateRangeValue({
          start: toZoned(startDate, project.timezone),
          end: toZoned(endDate, project.timezone),
        });

        setPeriod({
          from: startDate.toDate(project.timezone).getTime(),
          to: endDate.toDate(project.timezone).getTime(),
        });
      },
      [presets, dateBoundaries, dateRangeValue, project.timezone, setPeriod],
    );

    useEffect(() => {
      setDateRangeValue({
        start: parseAbsolute(new Date(period.from).toISOString(), project.timezone),
        end: parseAbsolute(new Date(period.to).toISOString(), project.timezone),
      });
    }, [period, project.timezone]);

    return (
      <div className={dateRangePickerLayout}>
        <DateRangePicker
          id=""
          presets={presets}
          popoverPlacement="bottom end"
          dateOptions={{
            "aria-label": "Analysis period",
            shouldCloseOnSelect: true,
            hideTimeZone: true,
            granularity: "day",
            minValue: dateBoundaries?.startDate,
            maxValue: today(project.timezone),
            value: dateRangeValue,
            isDateUnavailable: date => date.compare(dateBoundaries.startDate) < 0,
            onChange: onDateValueChange,
          }}
        />
        {dateTimeError && (
          <AdditionnalMessage className={datasetRangeMessage} status="ERROR" text={dateTimeError} />
        )}
        {dateTimeInfo && !dateTimeError && (
          <AdditionnalMessage className={datasetRangeMessage} status="INFO" text={dateTimeInfo} />
        )}
      </div>
    );
  },
);

export default PeriodSelector;

const datasetRangeMessage = css`
  margin-top: ${spacings.xs};
`;

const dateRangePickerLayout = css`
  display: flex;
  flex-direction: column;
  max-width: 300px;
`;
